import { useMemo } from "react"
import { MarkerType } from "@xyflow/react"

function hasSourceData(node, sourceType) {
  const sources = node.data?.sources?.[sourceType]
  if (!sources) return false

  switch (sourceType) {
    case "expected":
      return sources.tests?.length > 0 || sources.gaps?.length > 0
    case "actual":
      return sources.codeRefs?.length > 0
    case "desired":
      return sources.intent || sources.stories?.length > 0
    default:
      return false
  }
}

/**
 * Computes filtered/styled nodes and edges based on active filters, teams, and lens.
 */
export default function useFilteredNodes(nodes, edges, { filters, activeTeams, allTeams, activeLens, glossaryTerms, selectedEdge }) {
  const filteredNodes = useMemo(() => {
    return nodes.map((node) => {
      let opacity = 1

      // Lens label override (Phase 3)
      const lensLabelKey =
        activeLens === "behavior" ? "behaviorLabel" : activeLens === "data" ? "dataLabel" : "domainLabel"
      const lensLabel = node.data?.[lensLabelKey]

      if (lensLabel && typeof lensLabel === "string" && lensLabel.trim().length > 0) {
        node = {
          ...node,
          data: {
            ...node.data,
            label: lensLabel,
          },
        }
      }

      // Attach glossary terms for Domain lens highlighting
      if (activeLens === "domain" && glossaryTerms.length > 0) {
        node = {
          ...node,
          data: {
            ...node.data,
            glossaryTerms,
          },
        }
      }

      // Team-based filtering
      const nodeTeam = node.data?.team
      const allTeamsActive = activeTeams.length === allTeams.length
      if (nodeTeam && !allTeamsActive && !activeTeams.includes(nodeTeam)) {
        opacity = 0.2
      }

      // Source-based filtering (only if not already dimmed by team)
      if (opacity === 1) {
        const hasAnySources =
          hasSourceData(node, "expected") ||
          hasSourceData(node, "actual") ||
          hasSourceData(node, "desired")

        if (hasAnySources) {
          const hasAnySelectedSource =
            (filters.expected && hasSourceData(node, "expected")) ||
            (filters.actual && hasSourceData(node, "actual")) ||
            (filters.desired && hasSourceData(node, "desired"))

          const allFiltersOff = !filters.expected && !filters.actual && !filters.desired
          if (!allFiltersOff && !hasAnySelectedSource) {
            opacity = 0.3
          }
        }
      }

      if (opacity === 1) {
        return node
      }

      return {
        ...node,
        style: {
          ...node.style,
          opacity,
        },
      }
    })
  }, [nodes, filters, activeTeams, allTeams.length, activeLens, glossaryTerms])

  // Default edge options with arrow markers
  const defaultEdgeOptions = useMemo(() => ({
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 12,
      height: 12,
      color: '#64748b',
    },
    style: {
      strokeWidth: 2,
      stroke: '#64748b',
    },
  }), [])

  // Filter edges based on node visibility
  const filteredEdges = useMemo(() => {
    const visibleNodeIds = new Set(
      filteredNodes.filter((n) => !n.style?.opacity || n.style.opacity > 0.5).map((n) => n.id)
    )
    return edges.map((edge) => {
      const bothVisible = visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target)
      const isSelected = selectedEdge?.id === edge.id
      const arrowPosition = edge.data?.arrowPosition || 'end'
      const arrowColor = isSelected ? '#6366f1' : (bothVisible ? '#64748b' : 'rgba(100, 116, 139, 0.1)')

      const arrowMarker = {
        type: MarkerType.ArrowClosed,
        width: 12,
        height: 12,
        color: arrowColor,
      }

      const baseEdge = {
        ...edge,
        type: 'default',
        selected: isSelected,
        markerEnd: (arrowPosition === 'end' || arrowPosition === 'both') ? arrowMarker : undefined,
        markerStart: (arrowPosition === 'start' || arrowPosition === 'both') ? arrowMarker : undefined,
        style: {
          ...edge.style,
          strokeWidth: isSelected ? 3 : 2,
          stroke: isSelected ? '#6366f1' : '#64748b',
        },
        className: edge.data?.animated ? 'animated-edge' : '',
      }

      if (bothVisible) {
        return baseEdge
      }
      return {
        ...baseEdge,
        style: {
          ...baseEdge.style,
          opacity: 0.1,
        },
      }
    })
  }, [edges, filteredNodes, selectedEdge])

  return { filteredNodes, filteredEdges, defaultEdgeOptions }
}
